import { FC } from 'react'
import { default as MuiPaper, PaperProps as MuiPaperProps } from '@mui/material/Paper'

type StylePropsWithoutBackground = Omit<
  MuiPaperProps['sx'],
  | 'background'
  | 'background-attachment'
  | 'background-clip'
  | 'background-color'
  | 'background-image'
  | 'background-origin'
  | 'background-position'
  | 'background-repeat'
  | 'background-size'
>

export type PaperProps = Omit<MuiPaperProps, 'sx'> & {
  backgroundTheme?: 'light' | 'dark'
} & { sx?: StylePropsWithoutBackground }

/**
 * The background of an application resembles the flat, opaque texture of a sheet of paper,
 * and an application's behavior mimics paper's ability to be re-sized, shuffled, and bound together in multiple sheets.
 *
 * Documentation: [https://mui.com/material-ui/react-paper]
 */

export const Paper: FC<PaperProps> = (props: PaperProps) => {
  const { sx: sxProps, backgroundTheme, ...rest } = props

  const userRootProps =
    (sxProps as StylePropsWithoutBackground & { '&.MuiPaper-root': Record<string, string> })?.[
      '&.MuiPaper-root'
    ] ?? {}

  const internalBackgroundProps = {
    background:
      backgroundTheme === 'dark'
        ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.14) 100%), #121212'
        : '#FFFFFF'
  }

  const rootProps = { ...userRootProps, ...internalBackgroundProps }

  const style = {
    ...sxProps,
    '&.MuiPaper-root': rootProps
  }

  return <MuiPaper sx={style} {...rest} />
}
